import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Droplet, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

const SEVERITY_TO_PRIORITY = { Severe: "urgent", Medium: "high", Low: "medium" };

const blank = {
  active_leak: "",
  severity: "",
  noticed_at: "",
  location: "",
  shutoff_used: "",
  shutoff_reason_not_used: "",
  mold_or_damage_visible: "",
  mold_description: "",
  contacted_insurance: "",
  insurance_details: "",
  insurance_followup: "",
  photo_paths: [],
};

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {["Yes", "No"].map(v => (
        <button key={v} type="button" data-testid={`${testid}-${v.toLowerCase()}`} onClick={() => onChange(v)}
          className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-all ${value === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}
        >{v}</button>
      ))}
    </div>
  );
}

function Q({ num, text, required, children, gate }) {
  if (gate === false) return null;
  return (
    <div className="space-y-2 py-3 border-b border-slate-100 last:border-b-0">
      <Label className="text-sm text-slate-800 leading-snug flex gap-1.5">
        <span className="font-mono-num text-slate-400 shrink-0">{num}</span>
        <span>{text}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      </Label>
      <div className="pl-7">{children}</div>
    </div>
  );
}

export default function PlumbingAssessmentDialog({ open, onOpenChange, properties, onCreated }) {
  const [propertyId, setPropertyId] = useState("");
  const [a, setA] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setA(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId || !a.active_leak || !a.severity) { toast.error("Please answer required questions"); return; }
    setBusy(true);
    try {
      await api.post("/maintenance", {
        property_id: propertyId,
        title: "Plumbing / Water Damage Assessment",
        description: `Severity: ${a.severity}. ${a.location ? "Location: " + a.location : ""}`.trim(),
        priority: SEVERITY_TO_PRIORITY[a.severity] || "medium",
        assessment_type: "plumbing",
        assessment: a,
        photo_paths: a.photo_paths || [],
      });
      toast.success("Assessment submitted");
      onOpenChange(false); setA(blank); setPropertyId("");
      onCreated?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plumbing-assessment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Droplet className="h-5 w-5 text-blue-600" />Plumbing / Water Damage Assessment</DialogTitle></DialogHeader>
        <Card className="bg-amber-50 border-amber-200 p-3 mb-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertOctagon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Water damage spreads fast. Please complete this form ASAP and shut off the water if you can do so safely.</span>
        </Card>
        <form onSubmit={submit} className="space-y-2">
          <div className="space-y-1.5 pb-2 border-b border-slate-100">
            <Label className="text-xs">Property *</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="pa-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Q num="1" text="Is there an active leak / water issue right now?" required>
            <YesNo testid="pa-active" value={a.active_leak} onChange={(v) => set("active_leak", v)} />
          </Q>
          <Q num="2" text="Severity (tenant's judgement)" required>
            <Select value={a.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="pa-severity"><SelectValue placeholder="Select severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Severe">Severe (flooding / structural risk)</SelectItem>
                <SelectItem value="Medium">Medium (steady drip / wet ceiling)</SelectItem>
                <SelectItem value="Low">Low (slow drip / minor)</SelectItem>
              </SelectContent>
            </Select>
          </Q>
          <Q num="3" text="Date & time noticed"><Input type="datetime-local" value={a.noticed_at} onChange={(e) => set("noticed_at", e.target.value)} /></Q>
          <Q num="4" text="Location of leak / damage (room, fixture, ceiling, etc.)"><Input value={a.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Kitchen sink supply line under cabinet" /></Q>
          <Q num="5" text="Was the property's water shut-off used?" required>
            <YesNo testid="pa-shutoff" value={a.shutoff_used} onChange={(v) => set("shutoff_used", v)} />
          </Q>
          <Q num="5.0" text="If No, why not? (couldn't locate, jammed, didn't know how, etc.)" gate={a.shutoff_used === "No"}>
            <Textarea rows={2} value={a.shutoff_reason_not_used} onChange={(e) => set("shutoff_reason_not_used", e.target.value)} />
          </Q>
          <Q num="6" text="Any visible mold or water-stained damage?">
            <YesNo testid="pa-mold" value={a.mold_or_damage_visible} onChange={(v) => set("mold_or_damage_visible", v)} />
          </Q>
          <Q num="6.0" text="Describe the mold / damage" gate={a.mold_or_damage_visible === "Yes"}>
            <Textarea rows={2} value={a.mold_description} onChange={(e) => set("mold_description", e.target.value)} placeholder="e.g. Brown ring on bedroom ceiling, ~2ft wide" />
          </Q>
          <Q num="7" text="Did the tenant contact the Rental Insurance Company?">
            <YesNo testid="pa-insurance" value={a.contacted_insurance} onChange={(v) => set("contacted_insurance", v)} />
          </Q>
          <Q num="7.0" text="Rep name, claim #, amount covered" gate={a.contacted_insurance === "Yes"}>
            <Textarea rows={2} value={a.insurance_details} onChange={(e) => set("insurance_details", e.target.value)} />
          </Q>
          <Q num="7.1" text="Action required — please contact insurance and record rep, claim #, date/time, response" gate={a.contacted_insurance === "No"}>
            <Textarea rows={2} value={a.insurance_followup} onChange={(e) => set("insurance_followup", e.target.value)} />
          </Q>
          <Q num="8" text="Photos of the damage (optional but recommended)">
            <PhotoUploader value={a.photo_paths} onChange={(v) => set("photo_paths", v)} testid="pa-photo" />
          </Q>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="pa-submit" type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800">{busy ? "Submitting…" : "Submit Assessment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
